import * as THREE from 'three';
import { MINIMAP } from '../constants/gameSettings.js';

export default class MinimapManager {
    constructor(scene, minimapSize = MINIMAP.SIZE) {
        this.scene = scene;
        this.minimapSize = minimapSize;
        this.minimapCamera = null;
        this.minimapRenderer = null;
        this.minimapContainer = null;
        this.roadMapGroup = null;
        this.busMarker = null;
        this.threatZones = [];
        this.threatOverlay = null;
        this.accessibilityPreset = 'default';
        this.threatPalettes = {
            default: {
                directBorder: '2px solid rgba(255,40,40,0.95)',
                directGlow: '0 0 8px rgba(255,40,40,0.9)',
                nearBorder: '2px solid rgba(255,150,40,0.9)',
                nearGlow: '0 0 8px rgba(255,150,40,0.8)'
            },
            highContrast: {
                directBorder: '2px solid rgba(20,95,255,0.98)',
                directGlow: '0 0 9px rgba(20,95,255,0.95)',
                nearBorder: '2px solid rgba(0,214,255,0.95)',
                nearGlow: '0 0 9px rgba(0,214,255,0.9)'
            },
            deuteranopia: {
                directBorder: '2px solid rgba(0,90,255,0.95)',
                directGlow: '0 0 8px rgba(0,90,255,0.9)',
                nearBorder: '2px solid rgba(0,176,255,0.92)',
                nearGlow: '0 0 8px rgba(0,176,255,0.85)'
            }
        };
    }
    
    setup() {
        // Create minimap container with larger size
        const minimapContainer = document.createElement('div');
        minimapContainer.style.position = 'fixed';
        minimapContainer.style.right = '20px';
        minimapContainer.style.bottom = '50px';
        minimapContainer.style.width = `${this.minimapSize}px`;
        minimapContainer.style.height = `${this.minimapSize}px`;
        minimapContainer.style.border = '2px solid #ffffff';
        minimapContainer.style.borderRadius = '5px';
        minimapContainer.style.overflow = 'hidden';
        minimapContainer.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
        minimapContainer.style.display = window.innerWidth >= 768 ? 'block' : 'none';
        document.body.appendChild(minimapContainer);
        this.minimapContainer = minimapContainer;

        // Create orthographic camera for minimap with wider view
        const aspect = 1;
        const d = MINIMAP.CAMERA.VIEW_DISTANCE; // Slightly reduced view distance for better marker visibility
        this.minimapCamera = new THREE.OrthographicCamera(-d * aspect, d * aspect, d, -d, 1, 1000);
        this.minimapCamera.position.set(0, MINIMAP.CAMERA.HEIGHT, 0); // Higher position for better overview
        this.minimapCamera.lookAt(0, 0, 0);
        this.minimapCamera.up.set(0, 0, -1); // Rotate so north is up
        
        // Configure minimap camera layers
        this.minimapCamera.layers.enable(0); // Enable default layer
        this.minimapCamera.layers.enable(1); // Enable minimap marker layer
        this.minimapCamera.layers.disable(2); // Disable cloud layer

        // Create renderer for minimap
        this.minimapRenderer = new THREE.WebGLRenderer({ 
            antialias: true, 
            alpha: true,
            logarithmicDepthBuffer: true
        });
        this.minimapRenderer.setSize(this.minimapSize, this.minimapSize);
        this.minimapRenderer.setClearColor(0x000033, 0.3); // Slight blue tint for better contrast
        minimapContainer.appendChild(this.minimapRenderer.domElement);

        this.createThreatOverlay();

        // Create road map visualization
        this.createRoadMap();

        // Add compass rose
        this.addCompassRose(minimapContainer);
        
        // Add title to minimap
        this.addMinimapTitle(minimapContainer);
        
        return {
            container: minimapContainer,
            camera: this.minimapCamera,
            renderer: this.minimapRenderer
        };
    }

    createThreatOverlay() {
        if (!this.minimapContainer) return;

        const overlay = document.createElement('div');
        overlay.style.position = 'absolute';
        overlay.style.inset = '0';
        overlay.style.pointerEvents = 'none';
        overlay.style.zIndex = '4';
        this.minimapContainer.appendChild(overlay);
        this.threatOverlay = overlay;
    }

    createRoadMap() {
        // Create a group for road map elements
        this.roadMapGroup = new THREE.Group();
        
        // Add the road map group to the scene
        this.scene.add(this.roadMapGroup);
        
        return this.roadMapGroup;
    }

    addCompassRose(container) {
        // Create compass rose container
        const compassContainer = document.createElement('div');
        compassContainer.style.position = 'absolute';
        compassContainer.style.top = '10px';
        compassContainer.style.right = '10px';
        compassContainer.style.width = '40px';
        compassContainer.style.height = '40px';
        compassContainer.style.color = 'white';
        compassContainer.style.fontSize = '12px';
        compassContainer.style.fontFamily = 'Arial, sans-serif';
        compassContainer.style.userSelect = 'none';

        // Add cardinal directions
        const directions = [
            { text: 'N', top: '0px', left: '50%' },
            { text: 'E', top: '50%', left: '100%' },
            { text: 'S', top: '100%', left: '50%' },
            { text: 'W', top: '50%', left: '0px' }
        ];

        directions.forEach(dir => {
            const dirElement = document.createElement('div');
            dirElement.textContent = dir.text;
            dirElement.style.position = 'absolute';
            dirElement.style.transform = 'translate(-50%, -50%)';
            dirElement.style.top = dir.top;
            dirElement.style.left = dir.left;
            compassContainer.appendChild(dirElement);
        });

        container.appendChild(compassContainer);
        
        return compassContainer;
    }
    
    addMinimapTitle(container) {
        // Create title element
        const titleEl = document.createElement('div');
        //titleEl.textContent = 'MAP';
        titleEl.style.position = 'absolute';
        titleEl.style.top = '10px';
        titleEl.style.left = '10px';
        titleEl.style.color = 'white';
        titleEl.style.fontSize = '14px';
        titleEl.style.fontFamily = 'Arial, sans-serif';
        titleEl.style.fontWeight = 'bold';
        titleEl.style.textShadow = '1px 1px 2px black';
        titleEl.style.userSelect = 'none';
        
        container.appendChild(titleEl);
        
        return titleEl;
    }
    
    createBusMarker(bus) {
        // Create a triangle to represent the bus on the minimap
        const markerGeometry = new THREE.ConeGeometry(2, 5, 3);
        markerGeometry.rotateX(Math.PI / 2); // Make cone point forward
        
        const markerMaterial = new THREE.MeshBasicMaterial({ color: 0xFF0000 });
        this.busMarker = new THREE.Mesh(markerGeometry, markerMaterial);
        
        // Set the marker layer to 1 (minimap layer)
        this.busMarker.layers.set(1);
        
        // Add to scene
        this.scene.add(this.busMarker);
        
        return this.busMarker;
    }
    
    updateMinimap(bus) {
        if (!this.minimapRenderer || !this.minimapCamera || !this.busMarker || !bus) return;
        
        // Update bus marker position and rotation
        if (bus.mesh) {
            this.busMarker.position.x = bus.mesh.position.x;
            this.busMarker.position.z = bus.mesh.position.z;
            
            // Set the bus marker's Y position slightly above the ground for visibility
            this.busMarker.position.y = 5;
            
            // Update marker rotation to match bus direction
            this.busMarker.rotation.y = bus.mesh.rotation.y;
        }
        
        // Update minimap camera position to follow bus
        if (bus.mesh) {
            this.minimapCamera.position.x = bus.mesh.position.x;
            this.minimapCamera.position.z = bus.mesh.position.z;
            this.updateThreatOverlay(bus.mesh.position);
        }
        
        // Render the minimap
        this.minimapRenderer.render(this.scene, this.minimapCamera);
    }

    setThreatZones(zones = []) {
        this.threatZones = zones.map((zone) => ({
            x: zone.x,
            z: zone.z,
            radius: zone.radius || 12,
            level: zone.level || 'near'
        }));
    }

    clearThreatZones() {
        this.threatZones = [];
        if (this.threatOverlay) {
            this.threatOverlay.innerHTML = '';
        }
    }

    setAccessibilityPreset(preset = 'default') {
        if (!this.threatPalettes[preset]) return;
        this.accessibilityPreset = preset;
    }

    updateThreatOverlay(busPosition) {
        if (!this.threatOverlay) return;
        this.threatOverlay.innerHTML = '';
        if (!busPosition || this.threatZones.length === 0) return;
        const palette = this.threatPalettes[this.accessibilityPreset] || this.threatPalettes.default;

        const d = MINIMAP.CAMERA.VIEW_DISTANCE;
        const toPx = (worldDelta) => (worldDelta / (d * 2)) * this.minimapSize;

        for (const zone of this.threatZones) {
            const dx = zone.x - busPosition.x;
            const dz = zone.z - busPosition.z;

            if (Math.abs(dx) > d || Math.abs(dz) > d) continue;

            const x = this.minimapSize / 2 + toPx(dx);
            const y = this.minimapSize / 2 + toPx(dz);
            const radiusPx = Math.max(6, toPx(zone.radius));

            const ring = document.createElement('div');
            ring.style.position = 'absolute';
            ring.style.left = `${x - radiusPx}px`;
            ring.style.top = `${y - radiusPx}px`;
            ring.style.width = `${radiusPx * 2}px`;
            ring.style.height = `${radiusPx * 2}px`;
            ring.style.borderRadius = '50%';
            ring.style.border = zone.level === 'direct' ? palette.directBorder : palette.nearBorder;
            ring.style.boxShadow = zone.level === 'direct' ? palette.directGlow : palette.nearGlow;
            this.threatOverlay.appendChild(ring);
        }
    }
}
